import { PiggyBank, ShoppingCart, Repeat, BarChart2, Download, Check, ArrowRight, Zap, Coffee, Home, Shield, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { T, LogoMark, Wordmark } from '../theme';
import { setPageMeta } from '../utils/pageMeta';
import GlobalFooterBar from './GlobalFooterBar';

const CATEGORIES = [
  { name: 'Groceries', color: '#16A34A' },
  { name: 'Dining Out', color: '#EA580C' },
  { name: 'Rent & Bills', color: T.tealDeep },
  { name: 'Subscriptions', color: '#7C3AED' },
  { name: 'Transport', color: '#0284C7' },
  { name: 'Health', color: '#DC2626' },
  { name: 'Entertainment', color: '#D97706' },
  { name: 'Shopping', color: '#BE185D' },
  { name: 'Travel', color: '#0891B2' },
];

const FEATURES = [
  {
    icon: ShoppingCart,
    label: 'Scan any receipt instantly',
    tip: 'Grocery runs, restaurant meals, online orders — snap the receipt and AI fills in the details in seconds',
  },
  {
    icon: Repeat,
    label: 'Monthly reports',
    tip: 'Create a new report each month to see exactly what you spent, broken down by category',
  },
  {
    icon: BarChart2,
    label: 'Group & filter',
    tip: 'Group by category, filter by date, and sort by amount to find patterns in your spending',
  },
  {
    icon: Coffee,
    label: 'Track every purchase',
    tip: 'Add expenses manually in seconds or scan a receipt — even small daily purchases add up',
  },
  {
    icon: Home,
    label: 'Separate accounts',
    tip: 'Keep household, personal, and trip expenses in separate named reports — never mix them up',
  },
  {
    icon: Download,
    label: 'Export for your records',
    tip: 'Download a CSV or spreadsheet to review in Excel, share with a partner, or keep on file',
  },
  {
    icon: Shield,
    label: 'Bank imports stay on your device',
    tip: 'Import bank or credit card statements with "Keep local" — data is AES-256 encrypted in your browser and never uploaded to any server',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'Open a monthly report',
    desc: 'Create a report called "May 2026" or "Groceries Q2". Takes five seconds. Switch between reports any time.',
  },
  {
    num: '2',
    title: 'Track as you spend',
    desc: 'Scan receipts on the spot or add expenses manually. AI categorizes everything — you just confirm.',
  },
  {
    num: '3',
    title: 'See where your money went',
    desc: 'Filter, group, and export. Share with a partner or financial advisor, or just keep it for yourself.',
  },
];

const TESTIMONIALS = [
  {
    quote: "I always wondered where my money was going. One month of tracking showed me I was spending $400 on subscriptions I'd forgotten about. Cancelled half of them.",
    name: 'Tara H.',
    role: 'Personal use, 2 years',
  },
  {
    quote: "My partner and I use it together — shared household report, each of us adds expenses on our own phone. Finally on the same page about money.",
    name: 'Ben & Camille F.',
    role: 'Household tracking',
  },
];

function GetStartedButton({ onSignUp, label = "Get Started — It's Free", size = 'lg' }) {
  const lg = size === 'lg';
  return (
    <button
      onClick={onSignUp}
      style={{
        padding: lg ? '15px 32px' : '12px 24px',
        borderRadius: 14, border: 'none',
        background: T.gradTeal, color: '#fff',
        fontSize: lg ? 16 : 14, fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      {label} <ArrowRight size={lg ? 18 : 15} />
    </button>
  );
}

function PersonalFinanceLandingPage({ onSignUp, onSignIn, onSkip, onPricing }) {
  useEffect(() => {
    setPageMeta({
      title: 'Personal Budget & Expense Tracking',
      description: 'Finally know where your money goes. Track daily spending by category, spot patterns, and share household budgets. Free forever, no credit card.',
      url: 'https://myspend.info/?page=personal',
    });
  }, []);
  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh', paddingBottom: 36 }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <Wordmark size={18} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={onPricing} style={{
          background: 'none', border: 'none',
          color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
          padding: '8px 12px', cursor: 'pointer',
        }}>
          Pricing
        </button>
          <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>
            Sign In
          </button>
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(5,196,176,0.3)',
          }}>
            Get Started Free
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '72px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>
              AI-POWERED PERSONAL FINANCE TRACKING
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(30px, 6vw, 52px)', fontWeight: 900,
            color: '#fff', margin: '0 0 20px', lineHeight: 1.15, letterSpacing: -1,
          }}>
            Finally know<br />
            <span style={{ color: T.tealBright }}>where your money goes.</span>
          </h1>

          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.65)', margin: '0 0 16px',
            lineHeight: 1.65, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Scan receipts, track daily spending, and see exactly where every dollar went —
            by category, by month, by whatever matters to you. No spreadsheets required.
          </p>

          <p style={{ fontSize: 15, fontWeight: 800, color: T.tealBright, margin: '0 0 36px', letterSpacing: 0.3 }}>
            Completely free. No credit card. No catch.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <GetStartedButton onSignUp={onSignUp} label="Start Tracking Free" />
            <button onClick={onSignIn} style={{
              padding: '15px 32px', borderRadius: 14,
              border: '1.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>

        </div>
      </section>

      {/* ── Category strip ───────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
          Track spending across every category
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', justifyContent: 'center', alignItems: 'center' }}>
          {CATEGORIES.map(c => (
            <span key={c.name} style={{ fontSize: 13, fontWeight: 800, color: c.color, opacity: 0.85 }}>{c.name}</span>
          ))}
        </div>
      </div>

      {/* ── Problem hook ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
          Most people guess at their spending.<br />
          <span style={{ color: T.tealDeep }}>The guess is almost always wrong.</span>
        </h2>
        <p style={{ fontSize: 16, color: '#6B7280', lineHeight: 1.65, maxWidth: 540, margin: '0 auto 40px' }}>
          Studies show people underestimate their discretionary spending by 40–50%.
          A few minutes of tracking each week is all it takes to close that gap —
          and make decisions based on what's actually happening, not what you think is happening.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['AI receipt scanning', 'Monthly reports', 'Spending by category', 'Free forever'].map(item => (
            <div key={item} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: T.tealPale, borderRadius: 20, padding: '6px 14px',
            }}>
              <Check size={13} color={T.tealDeep} strokeWidth={3} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.tealDeep }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 8px' }}>
            Built for real life, not spreadsheets
          </h2>
          <p style={{ fontSize: 15, color: '#9CA3AF', textAlign: 'center', marginBottom: 40 }}>
            Everything you need to understand your money. Nothing you don't.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {FEATURES.map(({ icon: Icon, label, tip }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                background: T.appBg, borderRadius: 14, padding: '18px 20px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={T.tealDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.navy, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 48px' }}>
          Up and running in under a minute
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 22, flexShrink: 0,
                background: T.gradTeal, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 900, color: '#fff',
                boxShadow: '0 4px 14px rgba(5,196,176,0.35)',
              }}>
                {step.num}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.navy, marginBottom: 6 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Privacy strip ────────────────────────────────────────────── */}
      <div style={{ background: '#F0FDF4', borderTop: '1px solid #BBF7D0', borderBottom: '1px solid #BBF7D0', padding: '24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={18} color="#15803d" />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#15803d' }}>Your financial data stays private</span>
          </div>
          {[
            'Import bank statements with one click',
            'Choose local-only — data never leaves your browser',
            'AES-256 encrypted on your device',
            'No selling, no sharing, ever',
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={13} color="#15803d" strokeWidth={3} />
              <span style={{ fontSize: 13, color: '#374151' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Free forever callout ─────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: 'rgba(5,196,176,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', border: '1px solid rgba(5,196,176,0.3)',
          }}>
            <PiggyBank size={30} color={T.tealBright} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: '#fff', margin: '0 0 16px' }}>
            Free. Forever. For real.
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 16 }}>
            Personal expense tracking is free with no time limits or hidden paywalls.
            Create reports, scan receipts, export to CSV — all without spending a cent.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginBottom: 32 }}>
            Optional paid plans unlock AI scanning credits, additional export formats, and sharing features.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Start Tracking Free" />
        </div>
      </section>

      {/* ── AI Bookkeeper callout ───────────────────────────────────── */}
      <section style={{ background: T.tealPale, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Sparkles size={30} color={T.tealDeep} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
            Finally understand where your money goes
          </h2>
          <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.65, marginBottom: 28 }}>
            Ask your AI Bookkeeper questions about your spending in plain English. Get clear, instant answers — not formulas, not pivot tables.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', marginBottom: 32 }}>
            {[
              '"Where did my money go this month?"',
              '"What did I spend on dining vs. groceries this year?"',
              '"Show a chart of my monthly spending over time"',
              '"Which category grew the most compared to last month?"',
            ].map(q => (
              <div key={q} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={T.tealDeep} />
                </div>
                <span style={{ fontSize: 14, color: '#374151', fontStyle: 'italic' }}>{q}</span>
              </div>
            ))}
          </div>
          <GetStartedButton onSignUp={onSignUp} label="Try It Free" />
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 40px' }}>
            Real people, real results
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                background: T.appBg, borderRadius: 16, padding: '24px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{ fontSize: 28, color: T.tealBright, fontWeight: 900, lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65, margin: '0 0 16px', fontStyle: 'italic' }}>
                  {t.quote}
                </p>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', textAlign: 'center', background: T.gradNavy }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, color: '#fff', margin: '0 0 16px', lineHeight: 1.2 }}>
            Spend with intention.<br />
            <span style={{ color: T.tealBright }}>Start tracking today.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
            No credit card. No commitment. Free forever for personal use.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now — It's Free" />
        </div>
      </section>

      <GlobalFooterBar />

    </div>
  );
}

export default PersonalFinanceLandingPage;
